import { replaceAccents } from './string';

/**
 * Detects theme keywords from a list of available themes that appear in the given lyrics
 * @param {string} lyrics - The lyrics text to search for themes
 * @param {string[]} availableThemes - Array of theme strings to search for
 * @returns {string[]} - Array of detected themes (unique, sorted)
 */
export const detectThemesInLyrics = (lyrics, availableThemes) => {
  if (!lyrics || !availableThemes || availableThemes.length === 0) {
    return [];
  }

  // Normalize lyrics: lowercase and remove accents
  const normalizedLyrics = replaceAccents(lyrics.toLowerCase());

  // Detect themes that appear in the lyrics
  const detectedThemes = new Set();

  availableThemes.forEach((theme) => {
    const normalizedTheme = replaceAccents(theme.toLowerCase());

    // Use word boundaries to match whole words only
    // This prevents "love" from matching "beloved" or "glove"
    const regex = new RegExp(`\\b${escapeRegExp(normalizedTheme)}\\b`, 'i');

    if (regex.test(normalizedLyrics)) {
      // Always return theme in lowercase
      detectedThemes.add(theme.toLowerCase());
    }
  });

  return Array.from(detectedThemes).sort();
};

/**
 * Detects themes from all parts of a song
 * @param {Array} parts - Array of song part objects with lyrics property
 * @param {string[]} availableThemes - Array of theme strings to search for
 * @returns {string[]} - Array of detected themes (unique, sorted)
 */
export const detectThemesInAllParts = (parts, availableThemes) => {
  if (!parts || parts.length === 0 || !availableThemes || availableThemes.length === 0) {
    return [];
  }

  // Combine all lyrics from all parts
  const allLyrics = parts.map((part) => part.lyrics || '').join(' ');

  return detectThemesInLyrics(allLyrics, availableThemes);
};

/**
 * Escapes special regex characters in a string
 * @param {string} string - String to escape
 * @returns {string} - Escaped string safe for regex
 */
const escapeRegExp = (string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};
